/**
 * tenantTheme.js
 * Aplica a cor primária/secundária do tenant globalmente via:
 *  1. CSS custom properties no :root (para SCSS que usa var())
 *  2. Injeção de <style> com !important (para SCSS compilado de alta especificidade)
 *
 * DESIGN DA SIDEBAR (fixo, inspirado no preview de customização):
 *  - Fundo: #283046 (slate escuro — NÃO usa cor primária)
 *  - Header: #1e2a3b
 *  - Item ativo: bg rgba(255,255,255,0.06) + borda esquerda na cor primária
 *  - Ícones: brancos outlined (sem fill colorido)
 */

// ── Helpers de cor ────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

function hexToHsl(hex) {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => Math.round((l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))) * 255);
  return `#${[f(0), f(8), f(4)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function adjustLightness(hex, delta) {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex(h, s, Math.max(0, Math.min(100, l + delta)));
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Injetor de estilo dinâmico ─────────────────────────────────────────────────

function injectStyle(id, css) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

// ── API pública ───────────────────────────────────────────────────────────────

/**
 * Aplica a cor primária do tenant em toda a interface.
 * @param {string} hex - Ex: "#b90e0e"
 */
export function applyTenantColor(hex) {
  if (!hex || !hex.startsWith('#')) return;

  const root = document.documentElement;
  const { r, g, b } = hexToRgb(hex);
  const darker    = adjustLightness(hex, -10);
  const lighter   = adjustLightness(hex, +15);

  // ── CSS custom properties ────────────────────────────────────────────
  root.style.setProperty('--primary',           hex);
  root.style.setProperty('--primary-dark',      darker);
  root.style.setProperty('--primary-rgb',       `${r}, ${g}, ${b}`);

  // Usados por CollapsibleFilters, viewPedidos, KanbanView, etc.
  root.style.setProperty('--sf-primary',        hex);
  root.style.setProperty('--sf-primary-dark',   darker);
  root.style.setProperty('--sf-primary-light',  rgba(hex, 0.10));
  root.style.setProperty('--sf-accent',         hex);
  root.style.setProperty('--sf-accent-dark',    darker);
  root.style.setProperty('--sf-accent-light',   rgba(hex, 0.12));
  root.style.setProperty('--sf-accent-bg',      rgba(hex, 0.10));
  root.style.setProperty('--sf-accent-bg-hover', rgba(hex, 0.20));


  // ── Injeção de estilo com valores diretos ──────────────────────────
  injectStyle('sf-tenant-primary', `
    /* ═══ Botões ════════════════════════════════════════════════════════ */
    .btn-primary,
    .btn-primary.disabled,
    .btn-primary:disabled {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
      color:            #fff   !important;
    }
    .btn-primary:hover,
    .btn-primary:not(:disabled):not(.disabled):active,
    .btn-primary:not(:disabled):not(.disabled).active,
    .show > .btn-primary.dropdown-toggle {
      background-color: ${darker} !important;
      border-color:     ${darker} !important;
      color:            #fff     !important;
    }
    .btn-primary:focus,
    .btn-primary.focus {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
      box-shadow: 0 0 0 0.2rem rgba(${r},${g},${b},0.4) !important;
    }
    .btn-outline-primary {
      color:        ${hex} !important;
      border-color: ${hex} !important;
    }
    .btn-outline-primary:hover,
    .btn-outline-primary:not(:disabled):not(.disabled):active {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
      color:            #fff   !important;
    }

    /* ═══ Tabelas — cabeçalho na cor primária ════════════════════════════ */
    .table thead th,
    .table:not(.table-dark) thead th,
    .table.table-dark thead th,
    table thead th {
      background-color: ${hex} !important;
      color:            #fff   !important;
      border-color:     ${darker} !important;
    }

    /* ═══ Links e texto primário ══════════════════════════════════════════ */
    .text-primary,
    a.text-primary {
      color: ${hex} !important;
    }

    /* ═══ Badges ══════════════════════════════════════════════════════════ */
    .badge-primary,
    .badge.badge-primary {
      background-color: ${hex} !important;
      color:            #fff   !important;
    }

    /* ═══ Formulários — foco ══════════════════════════════════════════════ */
    .form-control:focus,
    .custom-select:focus {
      border-color: ${hex}                      !important;
      box-shadow:   0 0 0 0.2rem rgba(${r},${g},${b},0.25) !important;
    }

    /* ═══ Checkboxes e radios ══════════════════════════════════════════════ */
    .custom-control-input:checked ~ .custom-control-label::before {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
    }

    /* ═══ Progress bars ═══════════════════════════════════════════════════ */
    .progress-bar,
    .progress-bar.bg-primary {
      background-color: ${hex} !important;
    }

    /* ═══ Pagination ══════════════════════════════════════════════════════ */
    .page-item.active .page-link {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
      color:            #fff   !important;
    }
    .page-link { color: ${hex} !important; }
    .page-link:hover { color: ${darker} !important; }

    /* ═══ Nav pills / tabs ════════════════════════════════════════════════ */
    .nav-pills .nav-link.active,
    .nav-pills .show > .nav-link {
      background-color: ${hex} !important;
      color:            #fff   !important;
    }
    .nav-tabs .nav-link.active,
    .nav-tabs .nav-item.show .nav-link {
      color:        ${hex} !important;
      border-color: ${hex} transparent transparent !important;
    }

    /* ═══ Borders e backgrounds primários ════════════════════════════════ */
    .border-primary { border-color: ${hex} !important; }
    .bg-primary,
    .bg-primary.card-header {
      background-color: ${hex} !important;
      color:            #fff   !important;
    }

    /* ═══ Spinners / loaders ══════════════════════════════════════════════ */
    .spinner-border.text-primary,
    .spinner-grow.text-primary {
      color: ${hex} !important;
    }

    /* ═══ Scrollbar ═══════════════════════════════════════════════════════ */
    ::-webkit-scrollbar-thumb {
      background-color: rgba(${r},${g},${b},0.40) !important;
    }
    ::-webkit-scrollbar-thumb:hover {
      background-color: rgba(${r},${g},${b},0.65) !important;
    }
  `);

  localStorage.setItem('tenant_cor_primaria', hex);
}

/**
 * Aplica a cor secundária do tenant.
 * @param {string} hex - Ex: "#0ea5e9"
 */
export function applyTenantSecondaryColor(hex) {
  if (!hex || !hex.startsWith('#')) return;

  const root = document.documentElement;
  const { r, g, b } = hexToRgb(hex);
  const darker  = adjustLightness(hex, -10);

  root.style.setProperty('--secondary',              hex);
  root.style.setProperty('--secondary-rgb',          `${r}, ${g}, ${b}`);
  root.style.setProperty('--sf-secondary',           hex);
  root.style.setProperty('--sf-btn-secondary-bg',    hex);
  root.style.setProperty('--sf-btn-secondary-hover', darker);

  injectStyle('sf-tenant-secondary', `
    /* ═══ Botão secundário ════════════════════════════════════════════════ */
    .btn-secondary {
      background-color: ${hex} !important;
      border-color:     ${hex} !important;
      color:            #fff   !important;
    }
    .btn-secondary:hover,
    .btn-secondary:not(:disabled):not(.disabled):active {
      background-color: ${darker} !important;
      border-color:     ${darker} !important;
      color:            #fff     !important;
    }
    .btn-outline-secondary {
      color:        ${hex} !important;
      border-color: ${hex} !important;
    }
    .btn-outline-secondary:hover {
      background-color: ${hex} !important;
      color:            #fff   !important;
    }

    /* ═══ Badges secundários ══════════════════════════════════════════════ */
    .badge-secondary,
    .badge.badge-secondary {
      background-color: ${hex} !important;
      color:            #fff   !important;
    }

    /* ═══ Backgrounds / textos secundários ════════════════════════════════ */
    .bg-secondary       { background-color: ${hex} !important; color: #fff !important; }
    .text-secondary     { color: ${hex} !important; }
    .border-secondary   { border-color: ${hex} !important; }
  `);

  localStorage.setItem('tenant_cor_secundaria', hex);
}

/**
 * Reseta as cores para o padrão azul (usado no logout / tela de login).
 * Não escreve no localStorage.
 */
export function resetTenantColors() {
  const DEFAULT_PRIMARY = '#2563eb';
  const root = document.documentElement;
  const { r, g, b } = hexToRgb(DEFAULT_PRIMARY);
  const darker  = adjustLightness(DEFAULT_PRIMARY, -10);

  root.style.setProperty('--primary',          DEFAULT_PRIMARY);
  root.style.setProperty('--primary-dark',     darker);
  root.style.setProperty('--primary-rgb',      `${r}, ${g}, ${b}`);
  root.style.setProperty('--sf-primary',       DEFAULT_PRIMARY);
  root.style.setProperty('--sf-primary-dark',  darker);
  root.style.setProperty('--sf-primary-light', rgba(DEFAULT_PRIMARY, 0.10));
  root.style.setProperty('--sf-accent',        DEFAULT_PRIMARY);
  root.style.setProperty('--sf-accent-dark',   darker);
  root.style.setProperty('--sf-accent-light',  rgba(DEFAULT_PRIMARY, 0.12));
  root.style.setProperty('--sf-accent-bg',     rgba(DEFAULT_PRIMARY, 0.10));
  root.style.setProperty('--sf-accent-bg-hover', rgba(DEFAULT_PRIMARY, 0.20));
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--sf-secondary');

  const primaryEl = document.getElementById('sf-tenant-primary');
  if (primaryEl) primaryEl.textContent = '';
  const secondaryEl = document.getElementById('sf-tenant-secondary');
  if (secondaryEl) secondaryEl.textContent = '';
}

/**
 * Carrega e aplica ambas as cores do localStorage imediatamente (sem esperar a API).
 * Só aplica se o cache pertence ao tenant atual (evita vazamento cross-tenant).
 */
export function applyTenantColorFromCache() {
  const currentSlug = localStorage.getItem('tenantSlug') || 'dkp';
  const cachedSlug  = localStorage.getItem('tenant_cached_slug');

  // Se o slug em cache não corresponde ao tenant atual, limpa e ignora
  if (cachedSlug && cachedSlug !== currentSlug) {
    localStorage.removeItem('tenant_cor_primaria');
    localStorage.removeItem('tenant_cor_secundaria');
    localStorage.removeItem('tenant_logo_url');
    localStorage.removeItem('tenant_cached_slug');
    return;
  }

  const primary   = localStorage.getItem('tenant_cor_primaria');
  const secondary = localStorage.getItem('tenant_cor_secundaria');
  if (primary)   applyTenantColor(primary);
  if (secondary) applyTenantSecondaryColor(secondary);
}
